/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190902 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';

import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { BehaviorSubject } from 'rxjs';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';

import { CargoItemTypesDaoService } from '../../dao/cargo-item-types-dao.service';
import { CargoItemTypesService } from '../cargo-item-types.service';
import { CargoItemType } from 'src/app/_model/cargo-item-types';
import { IMSId } from 'src/app/_model/ims-object';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { APILevel } from 'src/app/_services/sync/api-level';

/**
 * Loads CargoItemTypes
 */
@Injectable()
export class CargoItemTypesLogicService implements CargoItemTypesService {
  public cargoItemTypes$ = new BehaviorSubject<CargoItemType[]>([]);
  public initializeStatus$: BehaviorSubject<InitializeStatus> =
    new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private remoteActionService: RemoteActionService,
    private cargoItemTypesDaoService: CargoItemTypesDaoService,
    private authenticationService: AuthenticationService,
  ) {
    this.initialize();
  }

  // Public Function
  public getCargoItemTypeById(id: IMSId): CargoItemType {
    if (id === undefined) {
      return undefined;
    }

    return this.cargoItemTypes$.getValue().find((cargoItem) => {
      if (
        cargoItem.id.imsId === id.imsId &&
        cargoItem.id.secondaryId === id.secondaryId
      ) {
        return true;
      }
      return false;
    });
  }

  public getCargoItemTypeByExtId(extId: string): CargoItemType {
    if (extId === undefined) {
      return undefined;
    }

    return this.cargoItemTypes$.getValue().find((cargoItem) => {
      if (cargoItem.extId === extId && cargoItem.extId === extId) {
        return true;
      }
      return false;
    });
  }

  // Resetable
  public async reset() {
    this.cargoItemTypesDaoService.reset();
  }

  public async clear() {
    await this.cargoItemTypesDaoService.clear();
    this.cargoItemTypes$.next([]);
  }

  // Syncable
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> {
    await this.clear();
    await this.loadRemoteCargoItemTypes(apiLevel, syncKey);
  }

  public async presync(): Promise<void> {}
  public async postsync(): Promise<void> {}

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING);
    try {
      await this.loadLocalCargoItemTypes();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message,
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
    }
  }

  // Private Functions
  private async loadLocalCargoItemTypes() {
    try {
      this.logger.log(this.constructor.name, '- Load local CargoItemTypes');
      let cargoItemTypes: CargoItemType[] =
        await this.cargoItemTypesDaoService.getCargoItemTypes();

      this.cargoItemTypes$.next(cargoItemTypes);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local CargoItemTypes: ',
        error.message,
      );
      throw error;
    }
  }

  private async loadRemoteCargoItemTypes(apiLevel: APILevel, syncKey: string) {
    if (this.authenticationService.authenticationState.getValue()) {
      this.logger.log(
        this.constructor.name,
        '- Load new CargoItemTypes from IMS',
      );

      try {
        let cargoItemTypes: CargoItemType[] =
          await this.loadRemoteCargoItemTypeBatch(syncKey, []);

        this.logger.log(
          this.constructor.name,
          'Finished loading all cargoItemTypes (' +
            cargoItemTypes.length +
            ' Total)',
        );
        this.cargoItemTypes$.next(cargoItemTypes);
      } catch (error) {
        this.logger.error(
          this.constructor.name,
          'Error downloading CargoItemTypes: ',
          error.message,
        );
        throw error;
      }
    }
  }

  private async loadRemoteCargoItemTypeBatch(
    syncKey: string,
    cargoItemTypes: CargoItemType[],
    nextPart?: string,
  ): Promise<CargoItemType[]> {
    let cargoItemTypesData;
    if (nextPart) {
      cargoItemTypesData = await this.remoteActionService.sendRemoteAction(
        new RemoteAction({
          endpoint: 'syncnextpart',
          cachable: false,
          fifo: false,
          authenticationRequired: true,
          data: {
            nextid: nextPart,
            synckey: syncKey
          },
        }),
      );
    } else {
      cargoItemTypesData = await this.remoteActionService.sendRemoteAction(
        new RemoteAction({
          endpoint: 'synccargoitemtypes',
          cachable: false,
          fifo: false,
          authenticationRequired: true,
          data: {
            synckey: syncKey
          },
        }),
      );
    }

    let nextId: string;
    let nextCargoItemTypes: CargoItemType[] = [];
    for (const cargoItemTypeData of cargoItemTypesData) {
      if (cargoItemTypeData['#nextid']) {
        nextId = cargoItemTypeData['#nextid'];
      } else {
        nextCargoItemTypes.push(CargoItemType.fromDto(cargoItemTypeData));
      }
    }

    this.logger.log(
      this.constructor.name,
      'Downloaded Batch CargoItemTypes: ',
      nextCargoItemTypes.length,
      'Total',
    );

    await this.cargoItemTypesDaoService.saveCargoItemTypes(nextCargoItemTypes);

    let returnCargoItemTypes = cargoItemTypes.concat(nextCargoItemTypes);
    if (nextId) {
      this.logger.log(
        this.constructor.name,
        'Continue to download more cargoItemTypes with nextid',
        nextId,
      );
      return await this.loadRemoteCargoItemTypeBatch(
        syncKey,
        returnCargoItemTypes,
        nextId
      );
    }

    return returnCargoItemTypes;
  }

  async initializeCompleted(): Promise<void> {}
}
